import * as Howler from "howler";
import * as Logger from "js-logger";
import { Signal } from "signals";
import { SoundConfigSchema } from "slotworks/config/sound-config-schema";
import Layers from "slotworks/graphics/layers/layers";
import Loader from "slotworks/loader/loader";
import SettingsModel from "slotworks/model/settings/settings-model";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import { WindowFocusManager } from "slotworks/utils/browser-utils";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import deviceInfo from "slotworks/utils/device-info";
import { parseJSONMap } from "slotworks/utils/json-utils";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

export class SoundManager {
    public onMuteChange: Signal;
    public onLoadComplete: Signal;

    public autoMuteOnUnfocus: boolean = true;

    public baseVolume: number = 0.5;

    private soundAliases: Map<string, string>;

    private eventStartSounds: Map<string, SoundPlayConfig[]>;
    private eventStopSounds: Map<string, SoundStopConfig[]>;
    private terminators: Map<string, string>;

    private playingSounds: Map<string, PlayingSound[]> = new Map<string, PlayingSound[]>();
    private pausedSounds: Map<string, number> = new Map<string, number>();

    private soundSprite: Howl;
    private soundSpriteDefinition: IHowlSoundSpriteDefinition;

    private muted: boolean = true;
    private musicMuted: boolean = false;
    private soundFxMuted: boolean = false;
    private musicQueue: string[] = [];
    private fadingMusic: string;

    private volume: number = 1;

    private isLoading: boolean = false;
    private loaded: boolean = false;
    private focusMuted: boolean = false;
    private isUnlocked: boolean = false;

    private muteSaved: boolean;

    private soundsWaitingForLoad: Map<string, SoundPlayConfig> = new Map<string, SoundPlayConfig>();

    // Manually restart looping sounds when they're done, instead of using howler loop (Fixes Mac Safari bug)
    private manualLooping: boolean = false;

    constructor() {
        this.onMuteChange = new Signal();
        this.onLoadComplete = new Signal();

        this.loadSettings();

        Loader.soundUnlocked.add(() => this.onSoundUnlocked());

        if (deviceInfo.isSafari() || deviceInfo.isFireFox()) {
            this.manualLooping = true;
            // Hack fix for howler iOS "interrupted" state
            Timer.setInterval(() => {
                if (Howler.Howler.ctx && Howler.Howler.ctx.state && (Howler.Howler.ctx.state as any) === "interrupted") {
                    (Howler.Howler as any).state = "suspended";
                    (Howler.Howler as any)["_autoResume"]();
                }
            }, 0);
        }
    }

    public init(soundConfig: SoundConfigSchema) {
        this.registerSounds(soundConfig);
    }

    public setSoundSprite(sound: Howl, definition: IHowlSoundSpriteDefinition) {
        this.soundSpriteDefinition = definition;
        this.soundSprite = sound;

        this.playPendingSounds();

        this.isLoading = false;
        this.loaded = true;

        Howler.Howler.volume(this.volume * this.baseVolume);

        this.onLoadComplete.dispatch();

        if (!WindowFocusManager.visible) {
            this.unfocus();
        }

        this.verifySoundsAllExist();
    }

    public customEventContract(event: string): Contract<void> {
        return new Contract<void>((resolve) => {

            const soundsToPlay = this.eventStartSounds.get(event);
            const soundsToStop = this.eventStopSounds.get(event) || [];

            if (soundsToPlay) {
                const allSounds = soundsToPlay.map((soundPlayConfig) => {
                    return () => this.playSound(soundPlayConfig);
                });

                new Parallel(allSounds).then(() => resolve(null));

            } else if (event.slice(0, 13) === ("button_press_")) {
                this.event(SoundEvent.default_button_press);

                resolve(null);
            } else if (event.slice(0, 12) === ("button_over_")) {
                this.event(SoundEvent.default_button_over);

                resolve(null);
            }

            for (const soundStopConfig of soundsToStop) {
                const soundsExist = this.stopSound(soundStopConfig.id, soundStopConfig.fadeTime, soundStopConfig.pause);
                const terminatorsExist = this.terminators.get(soundStopConfig.id);

                if (soundsExist && terminatorsExist) {
                    const soundConfig = new SoundPlayConfig(this.terminators.get(soundStopConfig.id), false);
                    this.playSound(soundConfig).execute();
                }
            }
        });
    }

    public customEvent(event: string) {
        this.customEventContract(event).execute();
    }

    public event(event: SoundEvent, ...args: string[]) {
        let id: string = event;

        for (let i = 0; i < args.length; i++) {
            id = id.split("{" + i + "}").join(args[i]);
        }

        this.customEvent(id);
    }

    public setLoading() {
        Howler.Howler.unload();
        this.isLoading = true;
    }

    public getLoading() {
        return this.isLoading;
    }

    public mute() {
        this.setMute(true);
    }

    public unmute() {
        if (!this.loaded && !this.isLoading) {
            Loader.loadSounds().then(() => {
                this.event(SoundEvent.sounds_ready);
            });
        }

        this.setMute(false);
    }

    public unmuteLoadScreen(): Contract<void> {
        return new Contract<void>((resolve) => {
            if (!this.loaded && !this.isLoading) {

                Layers.Prompts.setScene("load_sounds").execute();

                Loader.loadSounds().then(() => {
                    if (Layers.Prompts.currentSceneIs("load_sounds")) {
                        Layers.Prompts.defaultScene().execute();
                    }

                    resolve(null);

                    this.event(SoundEvent.sounds_ready);
                });
            }

            this.setMute(false);
        });
    }

    public setSoundFxMute(mute: boolean) {
        this.soundFxMuted = mute;

        Services.storage.set("soundFxMuted", this.soundFxMuted);

        this.updateMute();
    }

    public setMusicMute(mute: boolean) {
        this.musicMuted = mute;

        Services.storage.set("musicMuted", this.musicMuted);

        this.updateMute();
    }

    public getMuted() {
        return this.muted;
    }

    public getSoundFxMuted() {
        return this.soundFxMuted;
    }

    public getMusicMuted() {
        return this.musicMuted;
    }

    public setVolume(volume: number) {
        this.volume = volume;

        Howler.Howler.volume(volume * this.baseVolume);

        Services.storage.set("volume", this.volume);
    }

    public getVolume() {
        return this.volume;
    }

    public focus() {
        if (this.autoMuteOnUnfocus) {
            this.focusMuted = false;
        }
        this.updateMute();
        Howler.Howler.mute(false);
    }

    public unfocus() {
        if (this.autoMuteOnUnfocus) {
            this.focusMuted = true;
        }
        this.updateMute();
        Howler.Howler.mute(true);
    }

    public isMutePreferenceSet() {
        return this.muteSaved;
    }

    private playPendingSounds() {
        this.soundsWaitingForLoad.forEach((soundPlayConfig: SoundPlayConfig) => {
            this.playSound(soundPlayConfig).execute();
        });
        this.soundsWaitingForLoad.clear();
    }

    private onSoundUnlocked() {
        this.isUnlocked = true;
        this.musicQueue.forEach((aliasId) => {
            const musicId = this.playingSounds.get(aliasId)[0].howlerId;
            if (!this.manualLooping) {
                this.soundSprite.loop(true, musicId as any);
            }
        });
    }

    private registerSounds(soundConfig: SoundConfigSchema) {

        this.soundAliases = new Map<string, string>();
        this.eventStartSounds = new Map<string, SoundPlayConfig[]>();
        this.eventStopSounds = new Map<string, SoundStopConfig[]>();

        for (const soundAlias of Object.keys(soundConfig.aliases)) {
            this.soundAliases.set(soundAlias, soundConfig.aliases[soundAlias]);
        }

        for (const key of Object.keys(soundConfig.events)) {
            const playSounds: SoundPlayConfig[] = [];

            const shortCode: string = SettingsModel.language.split("-")[0];
            const languageEvents = (soundConfig.languages && shortCode) ? soundConfig.languages[shortCode] : undefined;
            const languageVariantAvailable = languageEvents && key in languageEvents;

            const event = (languageVariantAvailable) ? languageEvents[key] : soundConfig.events[key];

            if (event.hasOwnProperty("start")) {
                throw new Error("Invalid sound config. 'start' should be 'play'");
            }

            if (event.play) {
                for (const sound of event.play) {
                    let playSound;
                    if (typeof sound === "string") {
                        playSound = new SoundPlayConfig(sound, false, 0, false);
                    } else {
                        playSound = new SoundPlayConfig(sound.id, sound.loop, sound.fadeTime, sound.singleton, sound.music, sound.volume, sound.sync);
                    }

                    playSounds.push(playSound);

                    if (!playSound.music && !this.soundAliases.has(playSound.id)) {
                        throw new Error("Play sound not found " + playSound.id + " for " + key);
                    }
                }
            }

            this.eventStartSounds.set(key, playSounds);

            const stopSounds: SoundStopConfig[] = [];

            if (event.stop) {
                for (const sound of event.stop) {
                    let stopSound;

                    if (typeof sound === "string") {
                        stopSound = new SoundStopConfig(sound, 0, false);
                    } else {
                        stopSound = new SoundStopConfig(sound.id, sound.fadeTime, sound.pause);
                    }

                    stopSounds.push(stopSound);

                    if (!this.soundAliases.has(stopSound.id)) {
                        throw new Error("Stop sound not found " + stopSound.id + " for " + key);
                    }
                }
            }

            this.eventStopSounds.set(key, stopSounds);
        }

        this.terminators = parseJSONMap<string>(soundConfig.terminators);
    }

    private setMute(muted: boolean) {
        const oldMuted = this.muted;
        this.muted = muted;

        if (oldMuted !== this.muted) {
            this.onMuteChange.dispatch(this.muted);
        }

        this.muteSaved = true;
        Services.storage.set("muted", this.muted);
        this.updateMute();
    }

    private getMutedForSound(soundConfig: SoundPlayConfig) {
        const aliasId = soundConfig.id;

        if (this.focusMuted) {
            return true;
        }

        let muted = this.muted;
        if (!muted) {
            if (soundConfig.music) {

                // Only play last music in list, unless currently fading
                if (this.musicQueue.indexOf(aliasId) < this.musicQueue.length - 1 && this.fadingMusic !== aliasId) {
                    muted = true;
                }

                if (this.musicMuted) {
                    muted = true;
                }
            } else {
                if (this.soundFxMuted) {
                    muted = true;
                }
            }
        }

        return muted;
    }

    private playSound(soundPlayConfig: SoundPlayConfig): Contract<void> {
        let contract = new Contract<void>((resolve) => {
            let aliasId = soundPlayConfig.id;

            if (!aliasId && soundPlayConfig.music) {
                if (!this.musicQueue.length) {
                    resolve(null);
                    return;
                }
                aliasId = this.musicQueue[this.musicQueue.length - 1];
            }

            const soundId = this.soundAliases.get(aliasId);
            let seekTime = 0;
            let fadeFrom = -1;

            if (soundPlayConfig.sync) {
                seekTime = this.getSeekTime(soundPlayConfig.sync, soundId);
            }

            if (!this.soundSprite) {
                if (soundPlayConfig.loop) {
                    this.soundsWaitingForLoad.set(aliasId, soundPlayConfig);
                }
                resolve(null);
                return;
            }

            if (soundPlayConfig.music) {
                const currentMusic = this.musicQueue[this.musicQueue.length - 1];

                if (this.musicQueue.indexOf(aliasId) === -1) {
                    this.musicQueue.push(aliasId);
                }

                // If music fades in, old music should fade out (cross fade)
                if (currentMusic && currentMusic !== aliasId && soundPlayConfig.fadeTime > 0) {
                    seekTime = this.getSeekTime(currentMusic, soundId);
                    fadeFrom = 0;
                    this.fadingMusic = currentMusic;
                    this.stopSound(currentMusic, soundPlayConfig.fadeTime);
                }
            }

            if (!this.playingSounds.has(aliasId)) {
                this.playingSounds.set(aliasId, []);
            }

            let howlerId: number;

            let playingSounds = this.playingSounds.get(aliasId);

            try {
                if (this.pausedSounds.has(aliasId)) {
                    // Reuse paused sound
                    howlerId = this.pausedSounds.get(aliasId);
                    this.soundSprite.off("fade", this.fadeCompletePause, howlerId);
                    this.soundSprite.off("fade", this.fadeCompleteStop, howlerId);
                    this.soundSprite.volume(1, howlerId);
                    if (this.isUnlocked || soundPlayConfig.music) {
                        this.soundSprite.play(howlerId);
                    }
                } else {
                    if (!playingSounds.length || !soundPlayConfig.music) {
                        if (playingSounds.length > 0 && soundPlayConfig.singleton) {
                            this.stopSound(aliasId);
                            playingSounds = this.playingSounds.get(aliasId);
                        }

                        // Create new sound instance
                        if (this.isUnlocked || soundPlayConfig.music) {
                            howlerId = this.soundSprite.play(soundId);
                            this.soundSprite.mute(this.getMutedForSound(soundPlayConfig), howlerId);
                            if (!this.manualLooping) {
                                this.soundSprite.loop(soundPlayConfig.loop, howlerId);
                            }
                            playingSounds.push(new PlayingSound(howlerId, soundPlayConfig));

                            // Sync sound with music if applicable
                            if (seekTime) {
                                this.soundSprite.seek(seekTime, howlerId);
                            }
                        }
                    }
                }
            } catch (e) {
                throw new Error("Error trying to play sound " + aliasId + "(" + soundPlayConfig.id + ")");
            }

            this.updateMute();

            for (const playingSound of playingSounds) {
                if (soundPlayConfig.fadeTime > 0) {
                    this.fade(fadeFrom === -1 ? playingSound.config.volume : fadeFrom,
                        soundPlayConfig.volume, soundPlayConfig.fadeTime, playingSound.howlerId, playingSound.config.id).execute();
                } else {
                    this.soundSprite.volume(soundPlayConfig.volume, playingSound.howlerId);
                }
                playingSound.config.volume = soundPlayConfig.volume;
            }

            if (!playingSounds && soundPlayConfig.fadeTime > 0) {
                this.fade(0, soundPlayConfig.volume, soundPlayConfig.fadeTime, howlerId, soundPlayConfig.id).execute();
            }

            const resolveContract = () => {
                if (contract) {
                    contract = null;
                    resolve(null);
                }
            };

            this.soundSprite.once("end", resolveContract, howlerId);
            if (this.manualLooping && soundPlayConfig.loop) {

                const manualLoop = () => {
                    try {
                        this.soundSprite.seek(0, howlerId);
                        this.soundSprite.play(howlerId);
                    } catch (e) {
                        this.soundSprite.off("end", manualLoop, howlerId);
                    }
                };

                this.soundSprite.on("end", manualLoop, howlerId);
            }
            this.soundSprite.once("stop", resolveContract, howlerId);
        });

        return contract;
    }

    private updateMute() {
        this.playingSounds.forEach((sounds: PlayingSound[]) => {
            for (const sound of sounds) {
                const howlerId = sound.howlerId;

                this.soundSprite.mute(this.getMutedForSound(sound.config), howlerId);
            }
        });
    }

    private stopSound(aliasId: string, fadeTime: number = 0, pause: boolean = false): boolean {
        let soundsToStop = this.playingSounds.get(aliasId);

        if (this.soundsWaitingForLoad.has(aliasId)) {
            this.soundsWaitingForLoad.delete(aliasId);
        }

        if (soundsToStop && soundsToStop.length > 0) {
            soundsToStop = [...soundsToStop];
            this.playingSounds.set(aliasId, []);

            for (const sound of soundsToStop) {

                if (sound.config.music) {
                    const musicIndex = this.musicQueue.indexOf(aliasId);
                    if (musicIndex !== -1) {
                        this.musicQueue.splice(musicIndex, 1);
                    }
                    if (this.musicQueue.length && fadeTime > 0) {
                        const newMusic = this.playingSounds.get(this.musicQueue[this.musicQueue.length - 1]);
                        if (newMusic && newMusic.length) {
                            this.fade(0, newMusic[0].config.volume, fadeTime, newMusic[0].howlerId, newMusic[0].config.id).execute();
                        }
                    }
                }

                if (pause) {
                    this.pausedSounds.set(aliasId, sound.howlerId);
                    this.playingSounds.get(aliasId).push(new PlayingSound(sound.howlerId, sound.config));
                } else if (this.pausedSounds.has(aliasId)) {
                    this.pausedSounds.delete(aliasId);
                }

                const completeCallback = pause ? this.fadeCompletePause : this.fadeCompleteStop;

                if (fadeTime === 0) {
                    completeCallback(sound.howlerId);
                } else {
                    const id = sound.howlerId;

                    this.fade(this.soundSprite.volume(id) as number, 0, fadeTime, sound.howlerId, sound.config.id).then(() => completeCallback(id));
                }
            }

            this.updateMute();

            return true;
        } else {
            return false;
        }
    }

    /**
     * Howlers fade is really tempromental. I suggest using this instead
     */
    private fade(from: number, to: number, duration: number, id: number, name: string) {
        this.soundSprite.volume(from, id);
        const obj = { v: from };
        return new Contract<void>((resolve) => {
            new TweenJS.Tween(obj).to({ v: to }, duration).onUpdate(() => this.soundSprite.volume(obj.v, id)).onComplete(() => resolve(null)).start();
        });
    }

    private fadeCompletePause = (howlerId: number) => {
        this.soundSprite.pause(howlerId);
        this.updateMute();
    }

    private fadeCompleteStop = (howlerId: number) => {
        this.soundSprite.stop(howlerId);
        this.updateMute();
    }

    private loadSettings() {
        const soundFxMuted = Services.storage.getBoolean("soundFxMuted");
        const musicMuted = Services.storage.getBoolean("musicMuted");
        const muted = Services.storage.getBoolean("muted");
        const volume = Services.storage.getNumber("volume");

        if (soundFxMuted !== null) {
            this.soundFxMuted = soundFxMuted;
        }

        if (musicMuted !== null) {
            this.musicMuted = musicMuted;
        }

        if (volume !== null) {
            this.setVolume(volume);
        }

        if (muted !== null) {
            this.muteSaved = true;
            this.setMute(muted);
        }
    }

    // Gets a seek time for target to begin at in order to sync perfectly with source
    private getSeekTime(source: string, target: string) {
        let seekTime: number = 0;
        const syncSoundAlias = this.soundAliases.get(source);
        const soundToSyncTo = this.playingSounds.get(source)[0];
        if (soundToSyncTo) {
            seekTime = this.soundSprite.seek(soundToSyncTo.howlerId) as number;
        }
        // Make seek time relative
        seekTime -= this.soundSpriteDefinition[syncSoundAlias][0] / 1000;
        seekTime += this.soundSpriteDefinition[target][0] / 1000;

        return seekTime;
    }

    private verifySoundsAllExist() {
        this.soundAliases.forEach((soundName: string) => {
            if (!this.soundSpriteDefinition[soundName]) {
                Logger.error("Missing sound: ", soundName);
            }
        });
    }
}

class SoundPlayConfig {
    constructor(
        public id: string,
        public loop: boolean,
        public fadeTime: number = 0,
        public singleton: boolean = false,
        public music: boolean = false,
        public volume: number = 1,
        public sync?: string
    ) { }
}

class SoundStopConfig {
    constructor(public id: string, public fadeTime: number = 0, public pause: boolean = false) {
    }
}

class PlayingSound {
    constructor(public howlerId: number, public config: SoundPlayConfig) {
    }
}
